/* eslint-disable no-nested-ternary, jsx-a11y/label-has-associated-control */

import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import Select from "react-select";
import "./ChallengeFilterReport.css";
import {
  MAX_DATE_RANGE_MONTHS,
  MIN_REPORT_DATE,
  MIN_REPORT_DATE_LABEL,
  getEndDateAfterStartChange,
  getEnteredDatesError,
  getMaxEndDate,
  getTodayDate,
  isValidDateValue,
  validateDateRange
} from "./reportDateUtils";

const SELECT_ALL_VALUE = "__ALL__";

const STATUS_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "FINISHED", label: "Finished" }
];

const ACTUAL_STATUS_OPTIONS = STATUS_OPTIONS.filter(
  option => option.value !== "ALL"
);

const INITIAL_FILTERS = {
  statuses: [],
  challenges: [],
  startDate: "",
  endDate: ""
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "48px",
    borderRadius: "12px",
    borderColor: state.isFocused
      ? "#2563eb"
      : "#dbe3ef",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#2563eb"
    }
  }),

  menu: provided => ({
    ...provided,
    borderRadius: "12px",
    overflow: "hidden",
    zIndex: 9999
  }),

  menuPortal: provided => ({
    ...provided,
    zIndex: 9999
  }),

  multiValue: provided => ({
    ...provided,
    borderRadius: "8px",
    backgroundColor: "#e0e7ff"
  }),

  multiValueLabel: provided => ({
    ...provided,
    color: "#4338ca",
    fontWeight: 600
  })
};

/**
 * Challenge report date range rules.
 * FINISHED (including ALL) requires both dates.
 */
const validateChallengeDateRange = (
  startDate,
  endDate,
  isDateRequired,
  todayDate
) =>
  validateDateRange(startDate, endDate, {
    isDateRequired,
    requiredMessage:
      "Start Date and End Date are required when " +
      "FINISHED status is selected.",
    todayDate
  });

const getChallengeArray = getChallengeList => {
  if (Array.isArray(getChallengeList)) {
    return getChallengeList;
  }

  if (Array.isArray(getChallengeList?.items)) {
    return getChallengeList.items;
  }

  if (Array.isArray(getChallengeList?.data)) {
    return getChallengeList.data;
  }

  if (
    Array.isArray(
      getChallengeList?.data?.challengeList
    )
  ) {
    return getChallengeList.data.challengeList;
  }

  return [];
};

const ChallengeFilterReport = ({
  getChallengeList = [],
  onFilterChange,
  onDownload,
  isChallengeLoading = false,
  isDownloading = false
}) => {
  const [filters, setFilters] = useState(
    INITIAL_FILTERS
  );

  const [error, setError] = useState("");

  /*
   * Name of the date input currently being edited.
   * Date errors are hidden while the user is typing.
   */
  const [activeDateField, setActiveDateField] =
    useState("");

  const startDateOnFocusRef = useRef("");
  const lastChallengeRequestKeyRef = useRef("");
  const onFilterChangeRef = useRef(onFilterChange);

  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  /*
   * A YYYY-MM-DD string, so it is stable between
   * renders and only changes when the day changes.
   */
  const todayDate = getTodayDate();

  const selectedStatusValues = useMemo(
    () =>
      filters.statuses.map(status => status.value),
    [filters.statuses]
  );

  /**
   * Dates are mandatory whenever FINISHED is selected.
   *
   * This includes:
   * 1. FINISHED only
   * 2. ACTIVE + FINISHED
   * 3. ALL
   */
  const isDateRequired =
    selectedStatusValues.includes("FINISHED");

  const isOnlyActiveSelected =
    selectedStatusValues.length === 1 &&
    selectedStatusValues.includes("ACTIVE");

  const isStartDateValid = isValidDateValue(
    filters.startDate,
    todayDate
  );

  const dateRangeError = validateChallengeDateRange(
    filters.startDate,
    filters.endDate,
    isDateRequired,
    todayDate
  );

  /*
   * Errors for values the user has actually entered
   * (not for missing values), shown once editing ends.
   */
  const enteredDatesError = getEnteredDatesError(
    filters.startDate,
    filters.endDate,
    todayDate
  );

  /**
   * Challenge API request for the current filters.
   * Empty when Status is missing or the date range is
   * incomplete/invalid, so no API call is made.
   */
  const challengeRequestKey = useMemo(() => {
    if (!selectedStatusValues.length || dateRangeError) {
      return "";
    }

    const filterPayload = {
      challengeStatus: selectedStatusValues
    };

    if (filters.startDate && filters.endDate) {
      filterPayload.startDate = filters.startDate;
      filterPayload.endDate = filters.endDate;
    }

    return JSON.stringify(filterPayload);
  }, [
    selectedStatusValues,
    dateRangeError,
    filters.startDate,
    filters.endDate
  ]);

  const challengeOptions = useMemo(() => {
    const challengeArray = getChallengeArray(
      getChallengeList
    );

    const formattedOptions = challengeArray
      .map(challenge => {
        const value =
          challenge.id ?? challenge.challengeId;

        const label =
          challenge.title ??
          challenge.challengeName;

        if (
          value === undefined ||
          value === null ||
          !label
        ) {
          return null;
        }

        return {
          value,
          label,
          id: value,
          originalData: challenge
        };
      })
      .filter(Boolean);

    if (!formattedOptions.length) {
      return [];
    }

    return [
      {
        value: SELECT_ALL_VALUE,
        label: "Select All"
      },
      ...formattedOptions
    ];
  }, [getChallengeList]);

  const selectableChallenges = useMemo(
    () =>
      challengeOptions.filter(
        option =>
          option.value !== SELECT_ALL_VALUE
      ),
    [challengeOptions]
  );

  const areAllChallengesSelected =
    selectableChallenges.length > 0 &&
    filters.challenges.length ===
      selectableChallenges.length;

  /**
   * End Date maximum is the earlier of:
   * 1. Start Date plus 12 months
   * 2. Today's date
   */
  const maxEndDate = getMaxEndDate(
    filters.startDate,
    todayDate
  );

  const isFormComplete =
    selectedStatusValues.length > 0 &&
    filters.challenges.length > 0 &&
    !dateRangeError;

  const validateFilters = () => {
    if (!filters.statuses.length) {
      return "Select at least one Challenge Status.";
    }

    const rangeError = validateChallengeDateRange(
      filters.startDate,
      filters.endDate,
      isDateRequired,
      getTodayDate()
    );

    if (rangeError) {
      return rangeError;
    }

    if (!filters.challenges.length) {
      return "Select at least one challenge.";
    }

    return "";
  };

  const handleStatusChange = (
    selectedOptions,
    actionMeta
  ) => {
    const updatedOptions = selectedOptions || [];
    const clickedOption = actionMeta?.option;

    let nextStatuses;

    /*
     * ALL works as a toggle.
     */
    if (clickedOption?.value === "ALL") {
      const allStatusesAlreadySelected =
        ACTUAL_STATUS_OPTIONS.every(option =>
          updatedOptions.some(
            selected =>
              selected.value === option.value
          )
        );

      nextStatuses = allStatusesAlreadySelected
        ? []
        : ACTUAL_STATUS_OPTIONS;
    } else {
      nextStatuses = updatedOptions.filter(
        option => option.value !== "ALL"
      );
    }

    setError("");

    setFilters(prev => ({
      ...prev,
      statuses: nextStatuses,

      /*
       * Clear challenges because the available
       * challenge list changes with the status.
       * Dates are kept: they are optional (not
       * forbidden) for ACTIVE only.
       */
      challenges: []
    }));
  };

  const handleChallengeChange = (
    selectedOptions,
    actionMeta
  ) => {
    const clickedOption = actionMeta?.option;

    /*
     * Select All works as a toggle.
     */
    if (
      clickedOption?.value === SELECT_ALL_VALUE
    ) {
      setError("");

      setFilters(prev => ({
        ...prev,
        challenges: areAllChallengesSelected
          ? []
          : selectableChallenges
      }));

      return;
    }

    const selectedChallenges = (
      selectedOptions || []
    ).filter(
      option =>
        option.value !== SELECT_ALL_VALUE
    );

    setError("");

    setFilters(prev => ({
      ...prev,
      challenges: selectedChallenges
    }));
  };

  /**
   * Store exactly what the native date input reports.
   *
   * While typing, the input can report "" or partial
   * years such as "0002-05-12". Those values are never
   * rewritten here, because writing a different value
   * back into a controlled date input resets what the
   * user is typing. Validation is derived from state.
   */
  const handleDateChange = event => {
    const { name, value } = event.target;

    setError("");

    setFilters(prev =>
      prev[name] === value
        ? prev
        : {
            ...prev,
            [name]: value
          }
    );
  };

  const handleDateFocus = event => {
    const { name } = event.target;

    setActiveDateField(name);

    if (name === "startDate") {
      startDateOnFocusRef.current =
        filters.startDate;
    }
  };

  /**
   * When Start Date editing is finished, clear End
   * Date only if Start Date actually changed to a
   * valid date and the existing End Date no longer
   * fits the allowed range.
   */
  const handleDateBlur = event => {
    const { name } = event.target;

    setActiveDateField("");

    if (name !== "startDate") {
      return;
    }

    const startDateBeforeEdit =
      startDateOnFocusRef.current;

    setFilters(prev => {
      const nextEndDate = getEndDateAfterStartChange(
        startDateBeforeEdit,
        prev.startDate,
        prev.endDate,
        todayDate
      );

      return nextEndDate === prev.endDate
        ? prev
        : { ...prev, endDate: nextEndDate };
    });
  };

  const handleDownloadReport = () => {
    const validationError = validateFilters();

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");

    const payload = {
      challengeStatus: selectedStatusValues,
      challengeList: filters.challenges.map(
        challenge => challenge.value
      )
    };

    if (
      filters.startDate &&
      filters.endDate
    ) {
      payload.startDate = filters.startDate;
      payload.endDate = filters.endDate;
    }

    onDownload?.(payload);
  };

  const handleResetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setError("");

    /*
     * Allow the same filters to fetch again after
     * an explicit reset.
     */
    lastChallengeRequestKeyRef.current = "";
  };

  /**
   * Update the Challenge List only when a complete
   * and valid Status/date combination changes.
   *
   * Temporary editing states produce no request key,
   * and returning to the previously requested values
   * does not trigger a duplicate API call.
   */
  useEffect(() => {
    if (
      !challengeRequestKey ||
      challengeRequestKey ===
        lastChallengeRequestKeyRef.current
    ) {
      return;
    }

    lastChallengeRequestKeyRef.current =
      challengeRequestKey;

    /*
     * The challenge list is being refreshed, so the
     * current selection no longer applies.
     */
    setFilters(prev =>
      prev.challenges.length
        ? { ...prev, challenges: [] }
        : prev
    );

    onFilterChangeRef.current?.(
      JSON.parse(challengeRequestKey)
    );
  }, [challengeRequestKey]);

  /**
   * Remove selected challenges that are no longer
   * available after the challenge list changes.
   */
  useEffect(() => {
    const availableChallengeValues = new Set(
      selectableChallenges.map(option =>
        String(option.value)
      )
    );

    setFilters(prev => {
      if (!prev.challenges.length) {
        return prev;
      }

      const validChallenges =
        prev.challenges.filter(challenge =>
          availableChallengeValues.has(
            String(challenge.value)
          )
        );

      if (
        validChallenges.length ===
        prev.challenges.length
      ) {
        return prev;
      }

      return {
        ...prev,
        challenges: validChallenges
      };
    });
  }, [selectableChallenges]);

  const isDateSelectionIncomplete =
    Boolean(selectedStatusValues.length) &&
    !challengeRequestKey;

  const isChallengeDisabled =
    !filters.statuses.length ||
    isDateSelectionIncomplete ||
    isChallengeLoading;

  const challengePlaceholder =
    !filters.statuses.length
      ? "Select Status First"
      : isDateSelectionIncomplete
        ? "Select Date Range First"
        : "Select Challenge(s)";

  const displayedError =
    error ||
    (activeDateField ? "" : enteredDatesError);

  return (
    <div className="vf-challenge-filter-card">
      <div className="vf-challenge-filter-header">
        <h2 className="vf-challenge-filter-title">
          Challenge Report Filters
        </h2>

        <p className="vf-challenge-filter-subtitle">
          Download idea data based on challenge status,
          selected challenges and date range.
        </p>
      </div>

      <div className="vf-challenge-filter-grid">
        <div className="vf-challenge-filter-item">
          <label
            htmlFor="challenge-status"
            className="vf-challenge-filter-label"
          >
            Challenge Status
            <span className="vf-required">*</span>
          </label>

          <Select
            inputId="challenge-status"
            isMulti
            closeMenuOnSelect={false}
            options={STATUS_OPTIONS}
            value={filters.statuses}
            styles={customSelectStyles}
            classNamePrefix="vf-challenge-select"
            placeholder="Select Status"
            onChange={handleStatusChange}
          />
        </div>

        <div className="vf-challenge-filter-item">
          <label
            htmlFor="challenge-start-date"
            className="vf-challenge-filter-label"
          >
            Start Date

            {isDateRequired && (
              <span className="vf-required">
                *
              </span>
            )}

            <span className="icon-info tooltip_v">
              <span
                title={`Select a start date from ${MIN_REPORT_DATE_LABEL} onwards.`}
                className="tooltiptext_v"
              >
                Select a start date from{" "}
                {MIN_REPORT_DATE_LABEL} onwards.
              </span>
            </span>
          </label>

          <input
            id="challenge-start-date"
            type="date"
            name="startDate"
            value={filters.startDate}
            min={MIN_REPORT_DATE}
            max={todayDate}
            className="vf-challenge-filter-input"
            onChange={handleDateChange}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
          />
        </div>

        <div className="vf-challenge-filter-item">
          <label
            htmlFor="challenge-end-date"
            className="vf-challenge-filter-label"
          >
            End Date

            {isDateRequired && (
              <span className="vf-required">
                *
              </span>
            )}

            <span className="icon-info tooltip_v">
              <span
                title={`End Date must be within ${MAX_DATE_RANGE_MONTHS} months of Start Date.`}
                className="tooltiptext_v"
              >
                End Date must be within{" "}
                {MAX_DATE_RANGE_MONTHS} months of
                Start Date.
              </span>
            </span>
          </label>

          <input
            id="challenge-end-date"
            type="date"
            name="endDate"
            value={filters.endDate}
            min={
              isStartDateValid
                ? filters.startDate
                : MIN_REPORT_DATE
            }
            max={maxEndDate}
            disabled={!isStartDateValid}
            className="vf-challenge-filter-input"
            onChange={handleDateChange}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
          />
        </div>

        <div className="vf-challenge-filter-item">
          <label
            htmlFor="challenge-list"
            className="vf-challenge-filter-label"
          >
            Challenge List
            <span className="vf-required">*</span>
          </label>

          <Select
            inputId="challenge-list"
            isMulti
            closeMenuOnSelect={false}
            isDisabled={isChallengeDisabled}
            isLoading={isChallengeLoading}
            options={challengeOptions}
            value={filters.challenges}
            styles={customSelectStyles}
            classNamePrefix="vf-challenge-select"
            placeholder={challengePlaceholder}
            noOptionsMessage={() =>
              !filters.statuses.length
                ? "Select a status first"
                : isDateSelectionIncomplete
                  ? "Select start and end dates first"
                  : "No challenges found"
            }
            onChange={handleChallengeChange}
          />
        </div>
      </div>

      {isOnlyActiveSelected && (
        <p className="vf-challenge-filter-info">
          Date range is optional for Active status.
          If you select a Start Date, you must also
          select an End Date.
        </p>
      )}

      {isDateRequired && (
        <p className="vf-challenge-filter-info">
          Start Date and End Date are mandatory when
          Finished status is selected. Start Date must
          be on or after {MIN_REPORT_DATE_LABEL}, and the
          selected date range cannot exceed{" "}
          {MAX_DATE_RANGE_MONTHS} months.
        </p>
      )}

      {displayedError && (
        <div
          className="vf-challenge-filter-error"
          role="alert"
        >
          {displayedError}
        </div>
      )}

      <div className="vf-challenge-filter-actions">
        <button
          type="button"
          className="vf-challenge-report-button"
          disabled={
            !isFormComplete ||
            isChallengeLoading ||
            isDownloading
          }
          onClick={handleDownloadReport}
        >
          {isDownloading
            ? "Downloading..."
            : "Download Challenge Report"}
        </button>

        <button
          type="button"
          className="vf-btn1 vf-btn-secondaryReport"
          disabled={isDownloading}
          onClick={handleResetFilters}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default ChallengeFilterReport;
